import { Color } from "@repo/app-types/constants/colors";
import type { Size } from "@repo/ui/helpers/helpers";
import { Pill } from "@repo/ui/Pills/pill";
import { PillSkeleton } from "@repo/ui/Pills/pillSkeleton";
import type { FC } from "react";

export interface OutcomeRatePillProps {
  size: Size;
  /**
   * The green outcome rate (0-1 range). `undefined` means the rate is absent
   * (no outcome data in the period) and renders as a muted `--`; `null` means
   * the outcome is genuinely inapplicable and renders as an "N/A" pill.
   */
  outcomeRate: number | null | undefined;
  /** Optional label to display after the percentage (e.g., "Enrolled Rate") */
  label?: string;
  isLoading?: boolean;
}

/**
 * Displays the "win rate" or green outcome percentage as a pill.
 * Similar to GradingPassRatePill but for outcome scores.
 */
export const OutcomeRatePill: FC<OutcomeRatePillProps> = (props) => {
  const { outcomeRate } = props;

  if (outcomeRate == null && props.isLoading) {
    return <PillSkeleton size={props.size} />;
  }

  if (outcomeRate === null) {
    return <Pill color={Color.Gray} size={props.size} label="N/A" />;
  }

  if (outcomeRate === undefined) {
    return <span className="text-muted-foreground">--</span>;
  }

  const percentage = `${(outcomeRate * 100).toFixed(0)}%`;
  const label = props.label ? `${percentage} ${props.label}` : percentage;

  return <Pill color={Color.Gray} size={props.size} label={label} />;
};
